import { inject } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { DeviceDetectorService, DeviceInfo } from 'ngx-device-detector';
import { defer, merge, of } from 'rxjs';
import {
  catchError,
  distinctUntilKeyChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import { LoadClaimV2Dashboard } from '@amfam/claim/dashboardV2/data-access/lib/+state/claim-v2-dashboard.actions';
import {
  LoadSummaries,
  SummariesLoadSuccess
} from '@amfam/policy/data-access/lib/+state/summaries/summaries.actions';
import {
  selectHasCommercialPolicies,
  selectHasPersonalPolicies,
  selectPolicyAnalytics
} from '@amfam/policy/data-access/lib/+state/summaries/summaries.selectors';
import {
  FetchDaDataSuccessAction,
  ProfileActionTypes,
  ProfileLoad
} from '@amfam/profile/data-access/lib/+state/profile.actions';
import { AnalyticsFacade } from '@amfam/shared/analytics/lib/+state/analytics.facade';
import { EventAnalyticParams } from '@amfam/shared/analytics/lib/models/analytics.model';
import { normalizeAccountType } from '@amfam/shared/user/lib/+state/account-type.util';
import { LoginSuccess } from '@amfam/shared/user/lib/+state/user.actions';
import { AccountTypeEnum, UserLogin } from '@amfam/shared/user/lib/+state/user.model';
import {
  selectLastLoggedInOn,
  selectTrackId,
  selectTypeOfAccountCode
} from '@amfam/shared/user/lib/+state/user.selectors';
import {
  LoadUserBrandDataFailure,
  LoadUserBrandDataSuccess
} from '@amfam/shared/utility/brand/lib/+state/brand.actions';
import { selectCustomerGroupId as selectBrandCustomerGroupId } from '@amfam/shared/utility/brand/lib/+state/brand.selectors';
import { BrandService } from '@amfam/shared/utility/brand/lib/services/brand.service';

import { startApplication } from '../app-bootstrap/app-bootstrap.actions';
import {
  loginUserSuccess,
  sessionLoad,
  loadAccount as sessionLoadAccount,
  sessionLoadSuccess
} from '../session/session.actions';

export const start = createEffect(
  (actions$ = inject(Actions), deviceService = inject(DeviceDetectorService)) => {
    return actions$.pipe(
      ofType(sessionLoad),
      switchMap(() => {
        const deviceInfo: DeviceInfo = deviceService.getDeviceInfo();
        return [
          sessionLoadSuccess({
            browser: deviceInfo.browser,
            browserVersion: deviceInfo.browser_version,
            device: deviceInfo.device,
            os: deviceInfo.os,
            osVersion: deviceInfo.os_version,
            userAgent: deviceInfo.userAgent,
            isMobile: deviceService.isMobile(),
            isTablet: deviceService.isTablet(),
            isDesktop: deviceService.isDesktop()
          }),
          startApplication()
        ];
      })
    );
  },
  { functional: true }
);

export const afterLogin = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(LoadUserBrandDataSuccess, LoadUserBrandDataFailure),
      map(action => action.payload),
      distinctUntilKeyChanged(
        'userId',
        // forgerockLogin() emits a placeholder identity with no userId (the real one arrives
        // from the digital account fetch), so this must tolerate undefined on either side.
        // Treating a missing userId as "not the same user" keeps repeat logins flowing through
        // rather than silently deduping them.
        (prev: string | undefined, next: string | undefined) =>
          !!prev && !!next && prev.toLowerCase() === next.toLowerCase()
      ),
      map(payload => sessionLoadAccount(payload))
    );
  },
  { functional: true }
);

export const getClaimsAfterLogin = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(sessionLoadAccount),
      map(action => action.payload),
      filter(payload => {
        // sessionLoadAccount fires once before the digital account fetch has resolved the
        // account type, so "not business" must mean "known to not be business". Without the
        // first check a business user briefly looks personal and loads the claims dashboard,
        // which they are not entitled to.
        if (!payload?.typeOfAccountCode) {
          return false;
        }
        // Check for BUSINESS or any business-related account type
        const isBusiness =
          payload.typeOfAccountCode === AccountTypeEnum.Business ||
          payload.typeOfAccountCode === AccountTypeEnum.CFR;
        return !isBusiness;
      }),
      map(() => LoadClaimV2Dashboard())
    );
  },
  { functional: true }
);

export const serveMandate = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(ProfileActionTypes.FETCH_PARTY_DATA_SUCCESS),
      withLatestFrom(store.select(selectTrackId), store.select(selectLastLoggedInOn))
    );
  },
  { functional: true, dispatch: false }
);

export const init = createEffect(
  () => {
    return defer(() => of(sessionLoad()));
  },
  { functional: true }
);

export const setuser = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(loginUserSuccess),
      map(action => action.payload),
      map(payload => LoginSuccess(payload))
    );
  },
  { functional: true }
);

export const loadForUser = createEffect(
  (
    actions$ = inject(Actions),
    analyticsFacade = inject(AnalyticsFacade),
    brandService = inject(BrandService),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(loginUserSuccess),
      map(action => action.payload),
      distinctUntilKeyChanged(
        'userId',
        // forgerockLogin() emits a placeholder identity with no userId (the real one arrives
        // from the digital account fetch), so this must tolerate undefined on either side.
        // Treating a missing userId as "not the same user" keeps repeat logins flowing through
        // rather than silently deduping them.
        (prev: string | undefined, next: string | undefined) =>
          !!prev && !!next && prev.toLowerCase() === next.toLowerCase()
      ),
      concatLatestFrom(() => store.select(selectBrandCustomerGroupId)),
      switchMap(([payload, customerGroupId]) => {
        // set analytic profile info
        analyticsFacade.setProfileInfo({
          trackID: payload.trackId,
          customerGroupId: customerGroupId
        });

        if (payload?.trackId) {
          sessionStorage.setItem('trackID', payload.trackId);
        }

        const eventAnalytic: EventAnalyticParams = {
          eventName: 'login success',
          eventStep: ''
        };
        analyticsFacade.trackEvent(eventAnalytic);
        datadogRum.setUser({ id: payload.trackId });
        return brandService.loadBrandForUser(customerGroupId).pipe(
          map(() => LoadUserBrandDataSuccess(payload)),
          catchError(() => of(LoadUserBrandDataFailure(payload)))
        );
      })
    );
  },
  { functional: true }
);

export const loadAccount = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(sessionLoadAccount),
      map(action => action.payload),
      filter(
        payload =>
          // Make sure we have a customerId before calling getPolicySummaries
          !!payload?.customerId && payload.customerId !== ''
      ),
      map(payload => LoadSummaries(payload))
    );
  },
  { functional: true }
);

/**
 * Folds GET /digitalaccounts/{waid} into the session. `forgerockLogin()` only establishes that
 * the ForgeRock token is valid and emits the session WAID plus the analytics id, so this is
 * what actually supplies the account identity: customerId, account type, name and email. Until
 * it runs, `loadAccount` (policies), `loadBillingForBusinessAccount` and the party fetch all
 * sit behind gates that an identity-less payload cannot pass.
 *
 * Runs once per session, gated on the store not yet having an account type. That covers
 * business accounts, whose customerId is legitimately absent, and stops a later digital account
 * fetch (the profile page does one) from re-running the whole pipeline.
 *
 * `typeOfAccountCode` is normalized because this API answers 'PERSONAL' | 'BUSINESS' while the
 * app branches on AccountTypeEnum - see normalizeAccountType for the mapping and why BUSINESS
 * becomes CFR.
 */
export const loadIdentityFromDigitalAccount = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(FetchDaDataSuccessAction),
      map(action => action.payload),
      concatLatestFrom(() => [
        store.select(selectTypeOfAccountCode),
        store.select(selectBrandCustomerGroupId)
      ]),
      filter(([, accountType]) => !accountType),
      mergeMap(([digitalAccount, , customerGroupId]) => {
        // Prefer the entry for the brand the user came in on; fall back to the account-level id.
        // Both can be missing (empty ciamUsers, no forgeRockId), leaving forgerockId unset.
        const forgeRockId =
          digitalAccount.ciamUsers?.find(ciamUser => ciamUser.customerGroupId === customerGroupId)
            ?.frId ?? digitalAccount.forgeRockId;
        const basePayload = {
          customerId: digitalAccount.customerId,
          // The digital account truncates userId to 15 characters, so the email address is the
          // only full login name available. loginName is derived from userId in the reducer.
          userId: digitalAccount.emailAddress || digitalAccount.userId,
          emailAddress: digitalAccount.emailAddress,
          WAID: digitalAccount.waid,
          forgeRockId,
          trackId: digitalAccount.trackId,
          typeOfAccountCode: normalizeAccountType(digitalAccount.typeOfAccountCode),
          businessName: digitalAccount.nameDetail?.businessName,
          firstName: digitalAccount.nameDetail?.firstName ?? '',
          lastName: digitalAccount.nameDetail?.lastName ?? '',
          displayName: digitalAccount.nameDetail?.firstName ?? '',
          greetingName:
            digitalAccount.nameDetail?.greetingName || digitalAccount.nameDetail?.firstName || ''
        } as UserLogin;

        // A personal account holding only commercial policies is reported as PERSONAL by the
        // digital account but behaves as a business one. isCommercial on the loaded policies is
        // the reliable signal, so refine the type once summaries arrive. Business accounts never
        // load summaries, so this never fires for them and cannot undo their CFR mapping.
        const refinedAccountType$ = actions$.pipe(
          ofType(SummariesLoadSuccess),
          take(1),
          concatLatestFrom(() => [
            store.select(selectHasCommercialPolicies),
            store.select(selectHasPersonalPolicies)
          ]),
          map(([, hasCommercial, hasPersonal]) =>
            LoginSuccess({
              ...basePayload,
              typeOfAccountCode:
                hasCommercial && !hasPersonal ? AccountTypeEnum.Business : AccountTypeEnum.Customer
            } as UserLogin)
          )
        );

        return merge(
          of(LoginSuccess(basePayload), sessionLoadAccount(basePayload)),
          refinedAccountType$
        );
      }),
      catchError((err, caught) => {
        return caught; // resubscribe to restart the stream
      })
    );
  },
  { functional: true }
);

export const loadAccountForProfile = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(sessionLoadAccount),
      map(action => action.payload),
      map(payload => ProfileLoad(payload))
    );
  },
  { functional: true }
);

export const trackSignInSuccess = createEffect(
  (
    actions$ = inject(Actions),
    analyticsFacade = inject(AnalyticsFacade),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(loginUserSuccess),
      switchMap(() => actions$.pipe(ofType(SummariesLoadSuccess), take(1))),
      concatLatestFrom(() => [store.select(selectPolicyAnalytics), store.select(selectTrackId)]),
      tap(([, policyAnalytics, trackId]) =>
        analyticsFacade.trackEvent({
          event: 'secure_sign_in_success',
          source_system_list: policyAnalytics.source_system_list,
          partner_id_list: policyAnalytics.partner_id_list,
          // These need to be the same value and have both for GA4 purposes
          track_id: trackId,
          user_id: trackId
        })
      )
    );
  },
  { functional: true, dispatch: false }
);
